import { layoutArchitecture, layoutCircuit } from "./layout.js";
import { edgeHandles, handleSet, portOf } from "./ports.js";

export {
  portSide,
  emittedHandleIds,
  portHandleDefs,
  portOf,
  handleSet,
  edgeHandles,
  parkFocusSheet,
  HANDLE_CAP,
} from "./ports.js";
export { cardSize, layoutArchitecture, layoutCircuit, architectureGap } from "./layout.js";

export function resolveLinkName(mode, link) {
  const net = (link.net || "").trim() || null;
  const lab = (link.linkLabel || link.link_label || "").trim() || null;
  const ep =
    (link.endpoint || "").trim() ||
    String(link.from || "").split(".").pop() ||
    null;
  const legacy = (link.label || "").trim() || null;
  const n = net || (mode === "label" ? null : legacy);
  const l = lab || (mode === "net" ? null : legacy);
  switch (mode) {
    case "off":
      return undefined;
    case "net":
      return n || l || ep || undefined;
    case "label":
      return l || n || ep || undefined;
    case "endpoint":
      return ep || l || n || undefined;
    default:
      return n || l || ep || undefined;
  }
}

export function layerOf(link) {
  const layer = String(link.layer || "").toLowerCase();
  const scope = String(link.scope || "").toLowerCase();
  if (link.isPower) return "power";
  if (layer === "internal" || scope === "local") return "internal";
  if (layer === "boundary" || scope === "hierarchical") return "boundary";
  if (layer === "global" || scope === "global") return "power";
  return "boundary";
}

export function edgeStroke(layer) {
  if (layer === "internal") return "#64748b";
  if (layer === "power") return "#fbbf24";
  return "#60a5fa";
}

export function moduleId(ep) {
  const s = String(ep || "");
  const i = s.lastIndexOf(".");
  return i > 0 ? s.slice(0, i) : s;
}

/** Derive KiCad-like pages from scene (works with 1.0 and 1.1). */
export function derivePages(scene) {
  if (scene.pages && scene.pages.length) return scene.pages;
  const mods = scene.modules || [];
  const sheets = mods.filter(
    (m) => m.kind === "sheet" || m.depth === 0 || /domain|power|clock|connector/i.test(m.role || ""),
  );
  const top = mods.filter((m) => !m.parent);
  const base = top.length ? top : sheets;
  return base.map((m) => ({
    id: m.name,
    title: m.name,
    role: m.role,
    intent: m.intent || "",
    children: mods
      .filter((c) => c.parent === m.name || c.parent?.endsWith(`.${m.name}`))
      .map((c) => c.name),
    ports: (m.ports || []).map((p) => p.name),
    isRoot: /boardroot|root|^main$/i.test(m.name),
  }));
}

export function isRootPage(p) {
  return Boolean(p && (p.isRoot || p.is_root || /boardroot|root|^main$/i.test(p.id)));
}

export function architectureSheets(pages) {
  const kids = pages.filter((p) => !isRootPage(p));
  return kids.length ? kids : pages;
}

export function isMindScene(scene) {
  if (!scene || typeof scene !== "object") return false;
  if (Array.isArray(scene.modules)) return true;
  const ver = String(scene.version || "");
  return ver.startsWith("mind-scene");
}

export function architectureVisibleNames(scene, pages) {
  const byName = Object.fromEntries((scene.modules || []).map((m) => [m.name, m]));
  const root = (pages || []).find(isRootPage);
  const kids = (root?.children || []).map((n) => byName[n]).filter(Boolean);
  const instances = kids.filter((m) => m.kind === "sheet" || m.role === "sheet_instance");
  if (instances.length) return new Set(instances.map((m) => m.name));
  return new Set(architectureSheets(pages).map((p) => p.id));
}

export function liftToVisible(modName, visible, byName) {
  let n = modName;
  const seen = new Set();
  while (n && !visible.has(n) && !seen.has(n)) {
    seen.add(n);
    const p = byName[n]?.parent;
    if (!p) return null;
    n = String(p).split(".").pop();
  }
  return visible.has(n) ? n : null;
}

export function resolveEnterTarget(mod, pages) {
  const ids = new Set((pages || []).map((p) => p.id));
  if (!mod) return null;
  if (mod.role === "sheet_instance" && ids.has(mod.intent)) return mod.intent;
  if (mod.moduleRef && ids.has(mod.moduleRef)) return mod.moduleRef;
  const page = (pages || []).find((p) => p.id === mod.name);
  const kids = page?.children || mod.children || [];
  if (page && kids.length && !isRootPage(page)) return mod.name;
  if (mod.kind === "sheet" && ids.has(mod.intent) && mod.intent !== mod.name) return mod.intent;
  return null;
}

export function canEnterModule(mod, pages, focusSheet) {
  const t = resolveEnterTarget(mod, pages);
  return Boolean(t && t !== focusSheet);
}

function linkLayerAllowed(l, opts, pageMode) {
  const layer = layerOf(l);
  if (layer === "internal" && !opts.internal && pageMode === "architecture") return false;
  if (layer === "boundary" && !opts.boundary) return false;
  if ((layer === "power" || l.isPower) && !opts.power) return false;
  return true;
}

function sheetPortForHub(sheet, sheetEp, net, byName) {
  const names = handleSet(byName[sheet]);
  const raw = portOf(sheetEp);
  if (raw && names.has(raw)) return raw;
  if (net && names.has(net)) return net;
  return null;
}

/**
 * Leftover BoardRoot (or any isRoot page) star → sheet–sheet edges.
 * @returns {Array<{source,target,sourceHandle,targetHandle,net}>}
 */
export function pairRootHubEdges(links, visible, byName, opts, rootId) {
  const buckets = new Map();
  for (const l of links || []) {
    if (!linkLayerAllowed(l, opts, "architecture")) continue;
    const fromMod = moduleId(l.from);
    const toMod = moduleId(l.to);
    const fromLift = visible.has(fromMod) ? fromMod : liftToVisible(fromMod, visible, byName);
    const toLift = visible.has(toMod) ? toMod : liftToVisible(toMod, visible, byName);
    const fromVis = Boolean(fromLift && visible.has(fromLift) && fromLift !== rootId);
    const toVis = Boolean(toLift && visible.has(toLift) && toLift !== rootId);
    if (fromVis && toVis) continue;
    const fromIsRoot = fromMod === rootId || fromLift === rootId;
    const toIsRoot = toMod === rootId || toLift === rootId;
    if (!((fromIsRoot && toVis) || (toIsRoot && fromVis))) continue;

    const sheet = fromVis ? fromLift : toLift;
    const sheetEp = fromVis ? l.from : l.to;
    const rootEp = fromVis ? l.to : l.from;
    const net = (l.net || "").trim() || portOf(rootEp) || portOf(sheetEp);
    if (!net) continue;
    const port = sheetPortForHub(sheet, sheetEp, net, byName);
    if (!port) continue;
    if (!buckets.has(net)) buckets.set(net, new Map());
    const m = buckets.get(net);
    if (!m.has(sheet)) m.set(sheet, port);
  }

  const out = [];
  for (const [net, sheetMap] of buckets) {
    const sheets = [...sheetMap.keys()].sort((a, b) => a.localeCompare(b));
    for (let i = 0; i < sheets.length; i++) {
      for (let j = i + 1; j < sheets.length; j++) {
        const a = sheets[i];
        const b = sheets[j];
        const ha = sheetMap.get(a);
        const hb = sheetMap.get(b);
        if (!handleSet(byName[a]).has(ha) || !handleSet(byName[b]).has(hb)) continue;
        out.push({
          source: a,
          target: b,
          sourceHandle: ha,
          targetHandle: hb,
          net,
        });
      }
    }
  }
  return out;
}

const LABEL_STYLE = { fill: "#94a3b8", fontSize: 9, fontFamily: "ui-monospace, monospace" };
const LABEL_BG = { fill: "#0b1220", fillOpacity: 0.85 };

function architectureChrome(partial) {
  const stroke = edgeStroke("boundary");
  return {
    type: "smoothstep",
    animated: true,
    style: { stroke, strokeWidth: 1.8 },
    labelStyle: LABEL_STYLE,
    labelBgStyle: LABEL_BG,
    markerEnd: { type: "arrowclosed", width: 14, height: 14, color: stroke },
    ...partial,
  };
}

function circuitChrome(layer, partial) {
  const stroke = edgeStroke(layer);
  return {
    type: "smoothstep",
    animated: false,
    style: {
      stroke,
      strokeWidth: layer === "boundary" ? 1.6 : 1.2,
      strokeDasharray: layer === "power" ? "4 3" : undefined,
    },
    labelStyle: LABEL_STYLE,
    labelBgStyle: LABEL_BG,
    markerEnd: { type: "arrowclosed", width: 14, height: 14, color: stroke },
    ...partial,
  };
}

export function sceneToFlow(scene, opts = {}, filter, nameMode, pageMode, focusSheet) {
  const modules = scene.modules || [];
  const links = scene.links || [];
  const pages = derivePages(scene);
  const q = (filter || "").trim().toLowerCase();
  const byName = Object.fromEntries(modules.map((m) => [m.name, m]));

  let visibleNames = new Set();
  if (pageMode === "circuit" && focusSheet) {
    const page = pages.find((p) => p.id === focusSheet);
    const kids = page?.children || modules.filter((m) => m.parent === focusSheet).map((m) => m.name);
    visibleNames = new Set(kids);
    visibleNames.add(focusSheet);
  } else {
    visibleNames = architectureVisibleNames(scene, pages);
    if (!visibleNames.size) {
      modules.filter((m) => !m.parent).forEach((m) => visibleNames.add(m.name));
    }
  }

  const shown = modules.filter((m) => {
    if (!visibleNames.has(m.name)) return false;
    if (!q) return true;
    const blob = [m.name, m.role, m.intent, m.mpn, m.kind, m.subcircuitKind].join(" ").toLowerCase();
    return blob.includes(q);
  });

  let boxes;
  if (pageMode === "circuit" && focusSheet) {
    const focusMod = byName[focusSheet];
    const kids = shown.filter((m) => m.name !== focusSheet);
    boxes = layoutCircuit(focusMod, kids);
  } else {
    boxes = layoutArchitecture(shown);
  }

  const nodes = shown
    .map((m) => {
      const page = pages.find((p) => p.id === m.name);
      const isFocus = pageMode === "circuit" && m.name === focusSheet;
      const isSheetCard =
        pageMode === "architecture" ||
        isFocus ||
        m.kind === "sheet" ||
        m.role === "sheet_instance";
      const children = page?.children || m.children || [];
      const enterTarget = resolveEnterTarget(m, pages);
      const box = boxes.get(m.name) || { x: 0, y: 0, w: 220, h: 100 };
      return {
        id: m.name,
        type: isSheetCard ? "sheet" : "mind",
        position: { x: box.x, y: box.y },
        data: {
          name: m.name,
          role: m.role,
          intent: m.intent,
          kind: m.kind,
          depth: m.depth ?? 0,
          mpn: m.mpn,
          color: m.color,
          ports: m.ports || [],
          showPorts: opts.ports !== false,
          sheetId: m.sheetId,
          moduleRef: m.moduleRef,
          subcircuitKind: m.subcircuitKind || m.subcircuit_kind,
          children,
          parent: m.parent,
          entityRef: m.entityRef || m.entity_ref,
          canEnter: canEnterModule(m, pages, focusSheet),
          enterTarget,
          isRoot: isRootPage({ id: m.name, isRoot: page?.isRoot || page?.is_root }),
        },
        style: { width: box.w, height: box.h },
      };
    });

  const visible = new Set(nodes.map((n) => n.id));
  const edges = [];
  const seenNet = new Set();
  const rootId = (pages.find(isRootPage) || {}).id;

  if (pageMode === "architecture") {
    for (let i = 0; i < links.length; i++) {
      const l = links[i];
      if (!linkLayerAllowed(l, opts, pageMode)) continue;
      const fromMod = moduleId(l.from);
      const toMod = moduleId(l.to);
      const fromLift = visible.has(fromMod) ? fromMod : liftToVisible(fromMod, visible, byName);
      const toLift = visible.has(toMod) ? toMod : liftToVisible(toMod, visible, byName);
      if (!fromLift || !toLift || fromLift === toLift) continue;
      if (!visible.has(fromLift) || !visible.has(toLift)) continue;
      if (fromLift === rootId || toLift === rootId) continue;
      const handles = edgeHandles(l, fromLift, toLift, byName);
      if (!handles) continue;
      const netKey = (l.net || "").trim() || portOf(l.from);
      const dedupe = `${fromLift}\0${toLift}\0${netKey}`;
      if (seenNet.has(dedupe)) continue;
      seenNet.add(dedupe);
      edges.push(
        architectureChrome({
          id: `e${i}-${fromLift}.${handles.sourceHandle}-${toLift}.${handles.targetHandle}-${layerOf(l)}`,
          source: fromLift,
          target: toLift,
          sourceHandle: handles.sourceHandle,
          targetHandle: handles.targetHandle,
          label: resolveLinkName(nameMode || "auto", l),
          data: { layer: layerOf(l), from: l.from, to: l.to, net: l.net },
        }),
      );
    }
    const hubRows = pairRootHubEdges(links, visible, byName, opts, rootId);
    for (const row of hubRows) {
      const dedupe = `${row.source}\0${row.target}\0${row.net}`;
      if (seenNet.has(dedupe)) continue;
      seenNet.add(dedupe);
      edges.push(
        architectureChrome({
          id: `hub-${row.net}-${row.source}-${row.target}`,
          source: row.source,
          target: row.target,
          sourceHandle: row.sourceHandle,
          targetHandle: row.targetHandle,
          label: resolveLinkName(nameMode || "auto", { net: row.net }),
          data: { layer: "boundary", from: `${row.source}.${row.sourceHandle}`, to: `${row.target}.${row.targetHandle}`, net: row.net },
        }),
      );
    }
  } else {
    for (let i = 0; i < links.length; i++) {
      const l = links[i];
      if (!linkLayerAllowed(l, opts, pageMode)) continue;
      const fromMod = moduleId(l.from);
      const toMod = moduleId(l.to);
      if (!visible.has(fromMod) || !visible.has(toMod) || fromMod === toMod) continue;
      const handles = edgeHandles(l, fromMod, toMod, byName);
      if (!handles) continue;
      const layer = layerOf(l);
      edges.push(
        circuitChrome(layer, {
          id: `e${i}-${fromMod}.${handles.sourceHandle}-${toMod}.${handles.targetHandle}-${layer}`,
          source: fromMod,
          target: toMod,
          sourceHandle: handles.sourceHandle,
          targetHandle: handles.targetHandle,
          label: resolveLinkName(nameMode || "auto", l),
          data: { layer, from: l.from, to: l.to, net: l.net },
        }),
      );
    }
  }

  return { nodes, edges, pages };
}
